import React, { useState } from 'react';

const ImageUploadDialogs = ({
  showImageUploadDialog,
  setShowImageUploadDialog,
  showImageConfirmDialog,
  setShowImageConfirmDialog,
  imagePreview,
  imageFile,
  imageUploading,
  user,
  handleImageFileSelect,
  handleImageUpload,
  handleImageDelete,
  setImageFile,
  setImagePreview
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // 'idle', 'uploading', 'success', 'error'
  const [dragOver, setDragOver] = useState(false);

  // Enhanced file selection with drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      // Create a synthetic event object
      const syntheticEvent = {
        target: { files: [file] }
      };
      handleImageFileSelect(syntheticEvent);
    }
  };

  // Enhanced upload handler with progress simulation
  const handleUploadWithProgress = async () => {
    setUploadStatus('uploading');
    setUploadProgress(0);
    
    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + Math.random() * 20;
      });
    }, 200);

    try {
      await handleImageUpload();
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadStatus('success');
      
      // Auto close after success
      setTimeout(() => {
        setUploadStatus('idle');
        setUploadProgress(0);
      }, 1000);
    } catch (error) {
      clearInterval(progressInterval);
      setUploadStatus('error');
      setUploadProgress(0);
    }
  };

  const resetUploadState = () => {
    setUploadStatus('idle');
    setUploadProgress(0);
    setShowImageUploadDialog(false);
    setImageFile(null);
    setImagePreview(null);
  };

  return (
    <>
      {/* Image Upload Dialog */}
      {showImageUploadDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto transform animate-in zoom-in-95 duration-300">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-medium">Upload Profile Image</h2>
                  <p className="text-gray-600 text-sm">Choose a JPG or PNG image (max 5MB)</p>
                </div>
              </div>
              
              {imagePreview ? (
                <div className="mb-6">
                  <div className="relative">
                    <div className="w-40 h-40 mx-auto rounded-2xl overflow-hidden border-4 border-gray-100 shadow-lg">
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                    {uploadStatus === 'uploading' && (
                      <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center">
                        <div className="text-center text-white">
                          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                          <div className="text-sm font-normal">{Math.round(uploadProgress)}%</div>
                        </div>
                      </div>
                    )}
                    {uploadStatus === 'success' && (
                      <div className="absolute inset-0 bg-green-500/90 rounded-2xl flex items-center justify-center animate-in zoom-in duration-300">
                        <div className="text-center text-white">
                          <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <div className="text-sm font-normal">Success!</div>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="text-center text-sm text-gray-600 mt-3 font-normal">{imageFile?.name}</p>
                  <p className="text-center text-xs text-gray-500">{imageFile?.size ? `${(imageFile.size / 1024 / 1024).toFixed(2)} MB` : ''}</p>
                </div>
              ) : (
                <div className="mb-6">
                  <label 
                    className={`block w-full p-8 border-2 border-dashed rounded-2xl text-center cursor-pointer transition-all duration-300 ${
                      dragOver 
                        ? 'border-blue-500 bg-blue-50 scale-105' 
                        : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className={`transition-all duration-300 ${dragOver ? 'scale-110' : ''}`}>
                      <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-gray-600 font-normal block mb-2">
                        {dragOver ? 'Drop image here' : 'Click to select image'}
                      </span>
                      <span className="text-gray-500 text-sm">or drag and drop</span>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleImageFileSelect}
                      className="hidden"
                      disabled={uploadStatus === 'uploading'}
                    />
                  </label>
                </div>
              )}

              {/* Upload Progress Bar */}
              {uploadStatus === 'uploading' && (
                <div className="mb-6">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Uploading...</span>
                    <span>{Math.round(uploadProgress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                    <span>Only JPG and PNG files allowed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                    <span>Maximum file size: 5MB</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                    <span>Recommended size: 400x400 pixels</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={resetUploadState}
                  className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 font-normal"
                  disabled={uploadStatus === 'uploading'}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadWithProgress}
                  disabled={!imageFile || uploadStatus === 'uploading'}
                  className="px-6 py-3 text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-normal shadow-lg hover:shadow-xl"
                >
                  {uploadStatus === 'uploading' ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Uploading...</span>
                    </>
                  ) : uploadStatus === 'success' ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Success!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span>Upload Image</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Confirmation Dialog */}
      {showImageConfirmDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto transform animate-in zoom-in-95 duration-300">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-medium">Edit Profile Image</h2>
                  <p className="text-gray-600 text-sm">What would you like to do?</p>
                </div>
              </div>
              
              {user?.profile_image && (
                <div className="mb-6">
                  <div className="w-32 h-32 mx-auto rounded-2xl overflow-hidden border-4 border-gray-100 shadow-lg">
                    <img src={user.profile_image} alt="Current Profile" className="w-full h-full object-cover" />
                  </div>
                  <p className="text-center text-sm text-gray-600 mt-3 font-normal">Current profile image</p>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowImageConfirmDialog(false);
                    setShowImageUploadDialog(true);
                  }}
                  className="w-full px-6 py-4 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all duration-200 flex items-center justify-center gap-3 font-normal border border-blue-200 hover:border-blue-300"
                  disabled={imageUploading}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  </svg>
                  <span>Upload New Image</span>
                </button>
                
                <button
                  onClick={handleImageDelete}
                  disabled={imageUploading}
                  className="w-full px-6 py-4 text-red-700 bg-red-50 hover:bg-red-100 rounded-xl transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 font-normal border border-red-200 hover:border-red-300"
                >
                  {imageUploading ? (
                    <>
                      <div className="animate-spin h-5 w-5 border-2 border-red-600 border-t-transparent rounded-full"></div>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Remove Image</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => setShowImageConfirmDialog(false)}
                  className="w-full px-6 py-4 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 font-normal"
                  disabled={imageUploading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageUploadDialogs;